import { KeyValue, KeyValueDefault } from '@/main/models/common';
import {
  getTranslateByKey,
  isEmpty,
  isKeyValueDefault
} from '@/main/services/utils';
import { uppercaseFirstLetter } from '@/main/services/utilsString';
import { useVisualIdentityReducer } from '@/main/store/visualIdentity';
import { Clear, KeyboardArrowDown } from '@mui/icons-material';
import { Autocomplete, Paper, PaperProps } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import {
  Controller,
  FieldError,
  RegisterOptions,
  UseFormSetValue
} from 'react-hook-form';
import KogInputText from '../inputText/inputText';
import { Spinner, useStyles } from './styles';

interface IAutoCompleteProps {
  label: string;
  control: any;
  isLoading?: boolean;
  options: (KeyValue | KeyValueDefault)[] | null;
  onChangeCustom: (valueSelected: KeyValue | KeyValueDefault | null) => void;
  onDeleteCustom: (key: KeyValue | KeyValueDefault) => void;
  hasErrorCustom?: boolean;
  errorMessageCustom?: string;
  rulesCustom?: { required: boolean } & RegisterOptions;
  nameCustom: string;
  autoHighlightCustom?: boolean;
  setValueCustom?: UseFormSetValue<any>;
  valueDefaultKey?: string | number | null;
  isDefaultValueUnique?: boolean;
  disabledMessage?: boolean;
  autoOnChangeCustom?: boolean;
  hideValue?: boolean;
  isPluralLabel?: boolean;
  disableClearable?: boolean;
  disablePortal?: boolean;
  id?: string;
}

const CustomPaper = (props: PaperProps) => (
  <Paper
    elevation={8}
    {...props}
    className={`${props.className} ignore-click`}
  />
);

function KogAutoCompleteControlledWithDelete({
  label,
  control,
  isLoading,
  options,
  onChangeCustom,
  onDeleteCustom,
  hasErrorCustom,
  errorMessageCustom,
  nameCustom,
  rulesCustom = { required: false },
  autoHighlightCustom,
  setValueCustom,
  valueDefaultKey,
  isDefaultValueUnique = false,
  disabledMessage = false,
  hideValue = false,
  autoOnChangeCustom = false,
  isPluralLabel = false,
  id,
  ...props
}: IAutoCompleteProps) {
  const classes = useStyles();
  const getOptions = () => (options !== null ? options : []);
  const optionsDrop = getOptions();
  const renderRef = useRef(false);
  const [openAutocomplete, setOpenAutocomplete] = useState(false);

  const { visualIdentity } = useVisualIdentityReducer();

  const getOptionText = (
    option: KeyValue | KeyValueDefault | null | undefined
  ): string => {
    if (!option) return '';
    return String(option.value);
  };

  const renderErrorMessages = (error: FieldError | undefined) => {
    const requiredMessage = getTranslateByKey(
      isPluralLabel ? 'information.areRequired' : 'information.isRequired',
      true
    );
    const fieldMessage = getTranslateByKey('information.field');

    if (!error) return null;
    switch (error.type) {
      case 'required':
        return uppercaseFirstLetter(
          requiredMessage.replace('{0}', `${label || fieldMessage}`)
        );
      default:
        return error.message;
    }
  };

  useEffect(() => {
    if (autoHighlightCustom && setValueCustom && optionsDrop.length > 0) {
      setValueCustom(nameCustom, optionsDrop[0]);
    }
  }, [autoHighlightCustom]);

  useEffect(() => {
    if (
      !isLoading &&
      optionsDrop.length > 0 &&
      !isEmpty(valueDefaultKey) &&
      setValueCustom &&
      !renderRef.current
    ) {
      const findValue = optionsDrop.find(option =>
        isKeyValueDefault(option)
          ? String(option.default) === String(valueDefaultKey) ||
            option.default === 'True'
          : String(option.key) === String(valueDefaultKey)
      );
      setValueCustom(nameCustom, findValue);
      renderRef.current = true;
    }
  }, [isLoading, valueDefaultKey, JSON.stringify(optionsDrop)]);

  useEffect(() => {
    if (optionsDrop.length === 1 && setValueCustom && isDefaultValueUnique) {
      setValueCustom(nameCustom, optionsDrop[0]);
      if (autoOnChangeCustom) {
        onChangeCustom(optionsDrop[0]);
      }
    }
  }, [JSON.stringify(optionsDrop), isDefaultValueUnique]);

  return (
    <Controller
      control={control}
      name={nameCustom}
      rules={rulesCustom}
      render={({ field: { onChange, value }, fieldState }) => {
        return (
          <Autocomplete
            id={id}
            open={openAutocomplete}
            popupIcon={isLoading ? <Spinner /> : <KeyboardArrowDown />}
            className={`${classes.inputAutoComplete} ignore-click`}
            size="small"
            loading={isLoading}
            disabled={isLoading}
            options={optionsDrop}
            value={hideValue ? null : (value ?? null)}
            PaperComponent={CustomPaper}
            noOptionsText={getTranslateByKey('information.noOptions')}
            getOptionLabel={(option: KeyValue | KeyValueDefault) =>
              getOptionText(option)
            }
            onChange={(_, optionSelected) => {
              onChange(optionSelected);
              onChangeCustom(optionSelected);
            }}
            onOpen={() => setOpenAutocomplete(true)}
            onClose={() => setOpenAutocomplete(false)}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === 'clear') setOpenAutocomplete(false);
              if (newInputValue === '') setOpenAutocomplete(false);
            }}
            renderOption={(renderProps, option) => (
              <li {...renderProps}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    alignItems: 'center'
                  }}>
                  <span>{getOptionText(option)}</span>

                  <Clear
                    style={{
                      cursor: 'pointer',
                      fontSize: 30,
                      color: visualIdentity.colors.pantone2726C,
                      padding: 4
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      onDeleteCustom(option);
                    }}
                  />
                </div>
              </li>
            )}
            {...props}
            renderInput={params => (
              <KogInputText
                {...params}
                label={label}
                title={getOptionText(value)}
                variant="filled"
                id={id}
                error={!!hasErrorCustom || !!fieldState.error}
                helperText={
                  !disabledMessage &&
                  (errorMessageCustom || renderErrorMessages(fieldState.error))
                }
                required={rulesCustom?.required}
              />
            )}
          />
        );
      }}
    />
  );
}

export default KogAutoCompleteControlledWithDelete;
